/**
 * Messages Page
 * macOS Messages-style interface for SMS/iMessage conversations
 * Displays all messaging activity organized by lead
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lead, Activity, MediaItem, ENGAGEMENT_LETTER_TYPES, DEFAULT_ENGAGEMENT_TERMS, RETAINER_ENGAGEMENT_TERMS } from '../types';
import { useCRM } from '../utils/CRMContext';
import { useActivitiesForEntity } from '../hooks/useActivities';
import { MediaPicker } from '../components/media';
import SendblueTags from '../components/sms/SendblueTags';
import NewConversationModal from '../components/sms/NewConversationModal';
import { useAuth } from '../utils/AuthContext';
import {
  subscribeToAllMessages,
  sendSMS,
  SendblueMessage,
  isSendblueConfigured
} from '../utils/sendblue';
import { getSettings, subscribeToSettings } from '../utils/appSettings';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import {
  Search,
  Edit,
  Send,
  Paperclip,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Info,
  MessageSquare,
  Settings,
  ChevronLeft,
  X,
  MapPin,
  Mail,
  DollarSign,
  Calendar,
  Building,
  Clock,
  User,
  Voicemail,
  CreditCard,
  CheckCircle,
  Link2,
  Smile,
  ExternalLink,
} from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import HubQuickSearch from '../components/search/HubQuickSearch';
import InquireProjectInfo from '../components/leads/InquireProjectInfo';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchMessagesHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import './MessagesPage.css';

// Common emojis organized by category
const EMOJI_DATA = {
  'Smileys': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😋', '😎', '🤔', '🤗', '🤩', '😏'],
  'Gestures': ['👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '👋', '🤙', '💪', '🙏', '✋', '👊', '🤜', '🤛', '☝️', '👆', '👇', '👉'],
  'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '❣️', '💔', '🤍', '🤎', '♥️'],
  'Objects': ['📱', '💻', '📧', '📞', '🏠', '🚗', '✈️', '⏰', '📅', '💰', '💵', '📦', '🔧', '🔨', '📋', '📝', '✅', '❌', '⭐', '🎉'],
  'Nature': ['☀️', '🌙', '⭐', '🌈', '🔥', '💧', '🌊', '🌸', '🌺', '🌻', '🌴', '🍀', '🌲', '🍁', '🌾', '🐕', '🐈', '🦅', '🐝', '🦋'],
};

// Helper: we know URL is definitely NOT an image (e.g. .pdf, .doc)
// Sendblue uses storage.googleapis.com/inbound-file-store/... with non-standard extensions
const isDefinitelyNotImage = (url: string): boolean => {
  if (!url) return true;
  const nonImageExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.mp4', '.mov', '.wav', '.mp3'];
  const lowerUrl = url.toLowerCase();
  return nonImageExtensions.some(ext => lowerUrl.includes(ext));
};

interface Conversation {
  leadId: string;
  lead: Lead;
  messages: SendblueMessage[];
  lastMessage: SendblueMessage;
  unreadCount: number;
}

// Combined message/activity item for thread display
interface ThreadItem {
  type: 'message' | 'call' | 'inline-call' | 'inline-payment';
  timestamp: Date;
  data: SendblueMessage | Activity;
}

const STATUS_OPTIONS: { value: Lead['status']; label: string; bg: string; color: string }[] = [
  { value: 'new', label: 'New', bg: '#E3F2FD', color: '#1976D2' },
  { value: 'contacted', label: 'Contacted', bg: '#FFF3E0', color: '#E65100' },
  { value: 'qualified', label: 'Qualified', bg: '#E8F5E9', color: '#2E7D32' },
  { value: 'nurture', label: 'Nurture', bg: '#E0F7FA', color: '#00838F' },
  { value: 'lost', label: 'Lost', bg: '#FFEBEE', color: '#C62828' },
];

const MessagesPage: React.FC = () => {
  const { leads, updateLead, enhancedDeals, updateEnhancedDeal, createEngagementLetter } = useCRM();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [pendingSelection, setPendingSelection] = useState<{ leadId?: string; phoneNumber?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMobileList, setShowMobileList] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [paymentLink, setPaymentLink] = useState('');
  const [paymentLinks, setPaymentLinks] = useState<{ id: string; name: string; url: string }[]>([]);
  const [paymentRequestedStage, setPaymentRequestedStage] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const [isPaymentLinkMessage, setIsPaymentLinkMessage] = useState(false);
  const [showPaymentLinkSelector, setShowPaymentLinkSelector] = useState(false);
  const [showEngagementSelector, setShowEngagementSelector] = useState(false);
  const [creatingEngagement, setCreatingEngagement] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const isCreatingEngagementRef = useRef(false); // Synchronous guard against double-clicks

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const selectedLeadId = selectedConversation?.lead?.id;
  const { activities: leadActivities } = useActivitiesForEntity(
    selectedLeadId ? 'lead' : undefined,
    selectedLeadId,
  );

  // Check if Sendblue is configured
  useEffect(() => {
    const checkConfig = async () => {
      const configured = await isSendblueConfigured();
      setIsConfigured(configured);
      if (!configured) {
        setLoading(false);
      }
    };
    checkConfig();
  }, []);

  // Load QuickBooks payment link settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getSettings();
        if (settings) {
          setPaymentLink(settings.quickbooksPaymentLink || '');
          setPaymentLinks(settings.quickbooksPaymentLinks || []);
          setPaymentRequestedStage(settings.quickbooksPaymentRequestedStage || '');
          setBookingLink(settings.bookingLink || '');
        }
      } catch (error) {
        console.error('Error loading payment settings:', error);
      }
    };
    loadSettings();

    // Subscribe to settings changes
    const unsubscribe = subscribeToSettings((settings) => {
      if (settings.quickbooksPaymentLink !== undefined) {
        setPaymentLink(settings.quickbooksPaymentLink || '');
      }
      if (settings.quickbooksPaymentLinks !== undefined) {
        setPaymentLinks(settings.quickbooksPaymentLinks || []);
      }
      if (settings.quickbooksPaymentRequestedStage !== undefined) {
        setPaymentRequestedStage(settings.quickbooksPaymentRequestedStage || '');
      }
      if (settings.bookingLink !== undefined) {
        setBookingLink(settings.bookingLink || '');
      }
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to all messages and organize by lead
  useEffect(() => {
    if (!isConfigured) return;

    const unsubscribe = subscribeToAllMessages(
      (messages) => {
        // Group messages by lead
        const messagesByLead = new Map<string, SendblueMessage[]>();

        messages.forEach((msg) => {
          const leadId = msg.matchedLeadId;
          if (leadId) {
            if (!messagesByLead.has(leadId)) {
              messagesByLead.set(leadId, []);
            }
            messagesByLead.get(leadId)!.push(msg);
          }
        });

        // Build conversations
        const convos: Conversation[] = [];
        messagesByLead.forEach((msgs, leadId) => {
          const lead = leads.find((l) => l.id === leadId);
          if (lead && msgs.length > 0) {
            // Sort messages by date
            const sortedMsgs = [...msgs].sort(
              (a, b) => new Date(a.date_sent).getTime() - new Date(b.date_sent).getTime()
            );

            convos.push({
              leadId,
              lead,
              messages: sortedMsgs,
              lastMessage: sortedMsgs[sortedMsgs.length - 1],
              unreadCount: lead.hasUnreadMessage ? 1 : 0,
            });
          }
        });

        // Sort by most recent message
        convos.sort(
          (a, b) =>
            new Date(b.lastMessage.date_sent).getTime() -
            new Date(a.lastMessage.date_sent).getTime()
        );

        setConversations(convos);
        setLoading(false);

        // Update selected conversation if it exists
        if (selectedConversation) {
          const updated = convos.find((c) => c.leadId === selectedConversation.leadId);
          if (updated) {
            setSelectedConversation(updated);
          }
        }
      },
      (error) => {
        console.error('Error subscribing to messages:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isConfigured, leads, selectedConversation?.leadId]);

  // Handle navigation state - auto-select conversation from notification click
  useEffect(() => {
    const state = location.state as { selectedLeadId?: string; phoneNumber?: string } | null;
    if (state?.selectedLeadId || state?.phoneNumber) {
      setPendingSelection({ leadId: state.selectedLeadId, phoneNumber: state.phoneNumber });
      // Clear the state so refreshing doesn't re-select
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // When conversations load and we have a pending selection, select it
  useEffect(() => {
    if (pendingSelection && conversations.length > 0 && !selectedConversation) {
      let targetConvo: Conversation | undefined;

      // Try to find by leadId first
      if (pendingSelection.leadId) {
        targetConvo = conversations.find(c => c.leadId === pendingSelection.leadId);
      }

      // If not found by leadId, try by phone number
      if (!targetConvo && pendingSelection.phoneNumber) {
        const normalizedPhone = pendingSelection.phoneNumber.replace(/\D/g, '').slice(-10);
        targetConvo = conversations.find(c => {
          const leadPhone = c.lead.phone?.replace(/\D/g, '').slice(-10);
          return leadPhone === normalizedPhone;
        });
      }

      if (targetConvo) {
        setSelectedConversation(targetConvo);
        setShowMobileList(false); // Show the conversation on mobile
      }

      setPendingSelection(null);
    }
  }, [pendingSelection, conversations, selectedConversation]);

  // Get call activities for the selected lead and merge with messages
  const threadItems = useMemo((): ThreadItem[] => {
    if (!selectedConversation) return [];

    const items: ThreadItem[] = [];
    const processedCallIds = new Set<string>();

    // Add messages, inline calls, and inline payments
    selectedConversation.messages.forEach((msg) => {
      // Check if this is an inline call entry (from call_log webhook)
      const isInlineCall = msg.message_type === 'call' || msg.send_style === 'call';
      // Check if this is an inline payment entry (from QuickBooks webhook)
      const isInlinePayment = msg.message_type === 'payment' || msg.send_style === 'payment';

      if (isInlineCall) {
        // Track this call to avoid duplicates with activities
        processedCallIds.add(msg.id);
        items.push({
          type: 'inline-call',
          timestamp: new Date(msg.date_sent),
          data: msg,
        });
      } else if (isInlinePayment) {
        items.push({
          type: 'inline-payment',
          timestamp: new Date(msg.date_sent),
          data: msg,
        });
      } else {
        items.push({
          type: 'message',
          timestamp: new Date(msg.date_sent),
          data: msg,
        });
      }
    });

    // Add call activities (skip if already have an inline call for it)
    if (selectedConversation.lead.id) {
      const callActivities = leadActivities.filter((a) => a.type === 'call');

      callActivities.forEach((activity) => {
        // Skip if this activity was auto-logged and we already have the inline call
        // Check if activity.isAutomaticLog exists to avoid duplicates
        const activityData = activity as Activity & { isAutomaticLog?: boolean; callLogId?: string };
        if (activityData.isAutomaticLog && activityData.callLogId) {
          // Skip - we'll show the inline call instead
          return;
        }

        const timestamp = activity.completedAt || activity.scheduledAt || activity.createdAt;
        if (timestamp) {
          items.push({
            type: 'call',
            timestamp: timestamp instanceof Date ? timestamp : new Date(timestamp),
            data: activity,
          });
        }
      });
    }

    // Sort by timestamp
    items.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return items;
  }, [selectedConversation, leadActivities]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadItems]);

  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation?.lead.hasUnreadMessage && selectedConversation.lead.id) {
      updateLead(selectedConversation.lead.id, { hasUnreadMessage: false });
    }
  }, [selectedConversation?.leadId]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Insert emoji at cursor position
  const insertEmoji = (emoji: string) => {
    if (inputRef.current) {
      const start = inputRef.current.selectionStart || 0;
      const end = inputRef.current.selectionEnd || 0;
      const newText = newMessage.slice(0, start) + emoji + newMessage.slice(end);
      setNewMessage(newText);

      // Set cursor position after emoji
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = start + emoji.length;
          inputRef.current.selectionEnd = start + emoji.length;
          inputRef.current.focus();
        }
      }, 0);
    } else {
      setNewMessage(prev => prev + emoji);
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${conv.lead.firstName} ${conv.lead.lastName}`.toLowerCase();
    return (
      fullName.includes(query) ||
      conv.lead.email?.toLowerCase().includes(query) ||
      conv.lead.phone?.includes(query) ||
      conv.lastMessage.content.toLowerCase().includes(query)
    );
  });

  // Format timestamp for conversation list
  const formatListTime = (dateString: string): string => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else if (isThisWeek(date)) {
      return format(date, 'EEEE');
    } else {
      return format(date, 'M/d/yy');
    }
  };

  // Format timestamp for message thread
  const formatMessageTime = (date: Date): string => {
    return format(date, 'h:mm a');
  };

  // Format date separator
  const formatDateSeparator = (date: Date): string => {
    if (isToday(date)) {
      return 'Today';
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else if (isThisWeek(date)) {
      return format(date, 'EEEE');
    } else {
      return format(date, 'EEEE, MMMM d, yyyy');
    }
  };

  // Check if we need a date separator
  const needsDateSeparator = (current: ThreadItem, previous?: ThreadItem): boolean => {
    if (!previous) return true;
    const currentDate = current.timestamp.toDateString();
    const previousDate = previous.timestamp.toDateString();
    return currentDate !== previousDate;
  };

  // Handle media selection
  const handleMediaSelect = (item: MediaItem) => {
    setSelectedMedia(item);
    // Append media URL to message
    setNewMessage((prev) => {
      const mediaLine = `\n${item.name}: ${item.url}`;
      return prev ? prev + mediaLine : item.url;
    });
  };

  // Clear selected media
  const clearSelectedMedia = () => {
    setSelectedMedia(null);
  };

  // Handle loading payment link into message input
  const handleLoadPaymentLink = (linkUrl?: string) => {
    const url = linkUrl || paymentLink;
    if (!url && paymentLinks.length === 0) {
      alert('No payment link configured. Go to Settings → QuickBooks to set one up.');
      return;
    }
    // If we have multiple links and no specific one selected, show selector
    if (!linkUrl && paymentLinks.length > 1) {
      setShowPaymentLinkSelector(true);
      return;
    }
    // Use the provided link or the first available
    const finalUrl = url || (paymentLinks.length > 0 ? paymentLinks[0].url : '');
    if (finalUrl) {
      setNewMessage(finalUrl);
      setIsPaymentLinkMessage(true);
      setShowDetails(false);
      setShowPaymentLinkSelector(false);
      inputRef.current?.focus();
    }
  };

  // Select a specific payment link
  const handleSelectPaymentLink = (link: { id: string; name: string; url: string }) => {
    setNewMessage(link.url);
    setIsPaymentLinkMessage(true);
    setShowDetails(false);
    setShowPaymentLinkSelector(false);
    inputRef.current?.focus();
  };

  // Handle creating an engagement letter and loading link into message
  const handleCreateEngagement = async (engagementType: typeof ENGAGEMENT_LETTER_TYPES[0]) => {
    // Synchronous guard to prevent double-clicks
    if (isCreatingEngagementRef.current) {
      return;
    }

    if (!selectedConversation?.lead?.id || !user) {
      alert('Unable to create engagement letter');
      return;
    }

    isCreatingEngagementRef.current = true;
    const lead = selectedConversation.lead;
    setCreatingEngagement(true);
    setShowEngagementSelector(false);

    try {
      // Generate slug from lead name
      const lastName = lead.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const projectName = (lead.projectType || 'timber-frame').toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .split(' ')
        .slice(0, 2)
        .join('-');
      const slug = `${lastName}-${projectName}-${engagementType.id}`;

      // Generate 4-digit PIN
      const accessPin = Math.floor(1000 + Math.random() * 9000).toString();

      // Use appropriate terms based on letter type
      const terms = engagementType.id === 'retainer' ? RETAINER_ENGAGEMENT_TERMS : DEFAULT_ENGAGEMENT_TERMS;

      await createEngagementLetter({
        leadId: lead.id!,
        slug,
        accessPin,
        letterType: engagementType.id,
        title: `${engagementType.name} - ${lead.firstName} ${lead.lastName}`,
        clientName: `${lead.firstName} ${lead.lastName}`,
        clientEmail: lead.email,
        projectDescription: engagementType.description,
        designFee: engagementType.fee,
        deliverables: engagementType.deliverables,
        termsAndConditions: terms,
        status: 'sent',
        sentAt: new Date(),
      });

      const engagementUrl = `${window.location.origin}/engagement/${slug}`;

      // Load the engagement link + PIN into the message input
      setNewMessage(`Here's your ${engagementType.name} engagement letter:\n\n${engagementUrl}\n\nPIN: ${accessPin}`);
      setShowDetails(false);
      inputRef.current?.focus();

    } catch (error) {
      console.error('Error creating engagement letter:', error);
      alert('Error creating engagement letter');
    } finally {
      isCreatingEngagementRef.current = false;
      setCreatingEngagement(false);
    }
  };

  // Handle loading booking link into message input
  const handleLoadBookingLink = () => {
    if (!bookingLink) {
      alert('No booking link configured. Go to Settings → QuickBooks to set one up.');
      return;
    }
    setNewMessage(bookingLink);
    setShowDetails(false);
    inputRef.current?.focus();
  };

  // Handle sending a message
  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation?.lead.phone || sending) {
      return;
    }

    const messageToSend = newMessage.trim();
    const wasPaymentLinkMessage = isPaymentLinkMessage;

    setSending(true);
    try {
      const result = await sendSMS(
        selectedConversation.lead.phone,
        messageToSend,
        selectedConversation.lead.id,
        user?.id,
        user?.name,
        selectedConversation.lead.firstName,
        selectedConversation.lead.lastName
      );

      if (result.success) {
        setNewMessage('');
        setSelectedMedia(null);
        setIsPaymentLinkMessage(false);
        inputRef.current?.focus();

        // If this was a payment link message, move the deal to "Design" stage with "Payment Requested" sub-stage
        if (wasPaymentLinkMessage && selectedConversation.lead.id) {
          try {
            // Find the deal associated with this lead
            const leadDeal = enhancedDeals.find(d => d.leadId === selectedConversation.lead.id);
            if (leadDeal && leadDeal.id) {
              await updateEnhancedDeal(leadDeal.id, {
                stageId: 'design',
                designSubStage: 'payment-requested' as any,
              });
              console.log('Deal moved to Design stage (Payment Requested sub-stage)');
            }
          } catch (error) {
            console.error('Error moving deal to Design stage:', error);
          }
        }
      } else {
        alert(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle key press in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Select a conversation
  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv);
    setShowMobileList(false);
    setShowDetails(false);
  };

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || showNewConversation,
  });

  const getMessagesQuickSearchResults = useCallback(
    (query: string) => searchMessagesHub(query, conversations),
    [conversations],
  );

  const handleMessagesQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      const conv = result.payload as Conversation;
      handleSelectConversation(conv);
    },
    [],
  );

  // Get initials for avatar
  const getInitials = (lead: Lead): string => {
    return `${lead.firstName?.[0] || ''}${lead.lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  // Format budget
  const formatBudget = (budget: number): string => {
    if (budget >= 1000000) {
      return `$${(budget / 1000000).toFixed(1)}M`;
    } else if (budget >= 1000) {
      return `$${(budget / 1000).toFixed(0)}K`;
    }
    return `$${budget}`;
  };

  // Format status
  const formatStatus = (status: string): string => {
    return status.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Not configured state
  if (!isConfigured && !loading) {
    return (
      <div className="messages-page">
        <AppHeader />
        <div className="messages-not-configured">
          <MessageSquare size={64} />
          <h2>Sendblue Not Configured</h2>
          <p>Configure your Sendblue API credentials in Settings to view SMS & iMessage conversations.</p>
          <button onClick={() => window.location.href = '/'} className="messages-config-btn">
            <Settings size={16} />
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-page">
      <AppHeader />
      <div className="messages-container">
        {/* Sidebar - Conversation List */}
        <div className={`messages-sidebar ${!showMobileList ? 'mobile-hidden' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-search">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              className="compose-btn"
              title="New Conversation"
              onClick={() => setShowNewConversation(true)}
            >
              <Edit size={18} />
            </button>
          </div>

          <div className="conversations-list">
            {loading ? (
              <div className="loading-conversations">
                <div className="loading-spinner" />
                <span>Loading conversations...</span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="no-conversations">
                <MessageSquare size={32} />
                <p>No conversations yet</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.leadId}
                  className={`conversation-item ${selectedConversation?.leadId === conv.leadId ? 'selected' : ''
                    } ${conv.unreadCount > 0 ? 'unread' : ''}`}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <div className="conversation-avatar">
                    {getInitials(conv.lead)}
                  </div>
                  <div className="conversation-content">
                    <div className="conversation-header">
                      <span className="conversation-name">
                        {conv.lead.firstName} {conv.lead.lastName}
                      </span>
                      <span className="conversation-time">
                        {formatListTime(conv.lastMessage.date_sent)}
                      </span>
                    </div>
                    <div className="conversation-preview">
                      {conv.lastMessage.is_outbound && (
                        <span className="preview-you">You: </span>
                      )}
                      <span className="preview-text">
                        {conv.lastMessage.content}
                      </span>
                    </div>
                  </div>
                  {conv.unreadCount > 0 && (
                    <div className="unread-badge">{conv.unreadCount}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main - Message Thread */}
        <div className={`messages-main ${showMobileList ? 'mobile-hidden' : ''} ${showDetails ? 'with-details' : ''}`}>
          {selectedConversation ? (
            <>
              {/* Thread Header */}
              <div className="thread-header">
                <button
                  className="back-btn mobile-only"
                  onClick={() => setShowMobileList(true)}
                >
                  <ChevronLeft size={24} />
                </button>
                <div className="thread-avatar">
                  {getInitials(selectedConversation.lead)}
                </div>
                <div className="thread-info">
                  <span className="thread-name">
                    {selectedConversation.lead.firstName} {selectedConversation.lead.lastName}
                  </span>
                  {selectedConversation.lead.phone && (
                    <span className="thread-phone">{selectedConversation.lead.phone}</span>
                  )}
                </div>
                <div className="thread-actions">
                  <button
                    className={`action-btn ${showDetails ? 'active' : ''}`}
                    title="Lead Details"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    <Info size={18} />
                  </button>
                </div>
              </div>

              {/* Messages Thread */}
              <div className="thread-messages">
                {threadItems.map((item, index) => {
                  const prevItem = threadItems[index - 1];
                  const showDateSeparator = needsDateSeparator(item, prevItem);

                  // Render inline call (from Sendblue call_log webhook)
                  if (item.type === 'inline-call') {
                    const callMsg = item.data as SendblueMessage;
                    const callStatus = callMsg.call_status?.toLowerCase() || '';
                    const isMissed = callStatus === 'missed' || callStatus === 'no-answer';
                    const isVoicemail = callStatus === 'voicemail';

                    // Choose icon based on call direction and status
                    const CallIcon = isMissed ? PhoneMissed
                      : isVoicemail ? Voicemail
                        : callMsg.is_outbound ? PhoneOutgoing
                          : PhoneIncoming;

                    return (
                      <React.Fragment key={`inline-call-${callMsg.id}`}>
                        {showDateSeparator && (
                          <div className="date-separator">
                            <span>{formatDateSeparator(item.timestamp)}</span>
                          </div>
                        )}
                        <div className={`call-log inline-call ${isMissed ? 'missed' : ''} ${isVoicemail ? 'voicemail' : ''}`}>
                          <div className={`call-log-icon ${callMsg.is_outbound ? 'outbound' : 'inbound'} ${isMissed ? 'missed' : ''}`}>
                            <CallIcon size={14} />
                          </div>
                          <div className="call-log-content">
                            <span className="call-log-title">
                              {callMsg.is_outbound ? 'Outgoing Call' : 'Incoming Call'}
                            </span>
                            <span className="call-log-status">
                              {callMsg.call_status_display || callMsg.call_status || 'Call'}
                              {callMsg.call_duration_formatted && callMsg.call_duration_formatted !== '0:00' && (
                                <span className="call-duration"> • {callMsg.call_duration_formatted}</span>
                              )}
                            </span>
                            {callMsg.voicemail_url && (
                              <a
                                href={callMsg.voicemail_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="call-recording-link"
                              >
                                Play Voicemail
                              </a>
                            )}
                            {callMsg.recording_url && (
                              <a
                                href={callMsg.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="call-recording-link"
                              >
                                Play Recording
                              </a>
                            )}
                          </div>
                          <span className="call-log-time">
                            {formatMessageTime(item.timestamp)}
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  }

                  // Render inline payment (from QuickBooks webhook)
                  if (item.type === 'inline-payment') {
                    const paymentMsg = item.data as SendblueMessage;
                    return (
                      <React.Fragment key={`inline-payment-${paymentMsg.id}`}>
                        {showDateSeparator && (
                          <div className="date-separator">
                            <span>{formatDateSeparator(item.timestamp)}</span>
                          </div>
                        )}
                        <div className="payment-log">
                          <div className="payment-log-icon">
                            <CheckCircle size={14} />
                          </div>
                          <div className="payment-log-content">
                            <span className="payment-log-title">Payment Received</span>
                            <span className="payment-log-amount">
                              {paymentMsg.payment_amount_formatted || `$${paymentMsg.payment_amount?.toLocaleString() || '0'}`}
                            </span>
                            {paymentMsg.payment_method && (
                              <span className="payment-log-method">via {paymentMsg.payment_method}</span>
                            )}
                          </div>
                          <span className="payment-log-time">
                            {formatMessageTime(item.timestamp)}
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  }

                  // Render activity call (from CRM activities)
                  if (item.type === 'call') {
                    const activity = item.data as Activity;
                    return (
                      <React.Fragment key={`call-${activity.id}`}>
                        {showDateSeparator && (
                          <div className="date-separator">
                            <span>{formatDateSeparator(item.timestamp)}</span>
                          </div>
                        )}
                        <div className="call-log activity-call">
                          <div className="call-log-icon">
                            <Phone size={14} />
                          </div>
                          <div className="call-log-content">
                            <span className="call-log-title">
                              {activity.title || 'Phone Call'}
                            </span>
                            {activity.description && (
                              <span className="call-log-description">{activity.description}</span>
                            )}
                            {activity.outcome && (
                              <span className="call-log-outcome">{activity.outcome}</span>
                            )}
                          </div>
                          <span className="call-log-time">
                            {formatMessageTime(item.timestamp)}
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  }

                  const msg = item.data as SendblueMessage;
                  const isImessage = msg.send_style === 'imessage' && !msg.was_downgraded;

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSeparator && (
                        <div className="date-separator">
                          <span>{formatDateSeparator(item.timestamp)}</span>
                        </div>
                      )}
                      <div
                        className={`message ${msg.is_outbound ? 'outbound' : 'inbound'} ${isImessage ? 'imessage' : 'sms'
                          }`}
                      >
                        <div className="message-bubble">
                          {msg.content && <p>{msg.content}</p>}
                          {msg.media_url && (
                            <div className="message-media">
                              {!isDefinitelyNotImage(msg.media_url) ? (
                                <a href={msg.media_url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={msg.media_url}
                                    alt="Media attachment"
                                    className="media-image-preview"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <span className="media-link-fallback" style={{ display: 'none' }}>
                                    <Paperclip size={14} /> View Attachment
                                  </span>
                                </a>
                              ) : (
                                <a href={msg.media_url} target="_blank" rel="noopener noreferrer" className="attachment-link">
                                  <Paperclip size={14} /> View Attachment
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="message-time">
                          {formatMessageTime(item.timestamp)}
                          {msg.is_outbound && msg.status === 'delivered' && ' Delivered'}
                          {msg.is_outbound && msg.status === 'read' && ' Read'}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="thread-composer">
                {selectedMedia && (
                  <div className="selected-media-preview">
                    <span className="media-name">{selectedMedia.name}</span>
                    <button className="clear-media" onClick={clearSelectedMedia}>
                      <X size={14} />
                    </button>
                  </div>
                )}
                <button
                  className="attach-btn"
                  title="Attach media"
                  onClick={() => setShowMediaPicker(true)}
                  disabled={sending}
                >
                  <Paperclip size={20} />
                </button>
                <div className="emoji-picker-container" ref={emojiPickerRef}>
                  <button
                    className="emoji-btn"
                    title="Add emoji"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    disabled={sending}
                  >
                    <Smile size={20} />
                  </button>
                  {showEmojiPicker && (
                    <div className="emoji-picker">
                      {Object.entries(EMOJI_DATA).map(([category, emojis]) => (
                        <div key={category} className="emoji-category">
                          <div className="emoji-category-title">{category}</div>
                          <div className="emoji-grid">
                            {emojis.map((emoji, idx) => (
                              <button
                                key={idx}
                                className="emoji-item"
                                onClick={() => {
                                  insertEmoji(emoji);
                                  setShowEmojiPicker(false);
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="composer-input-wrapper">
                  <textarea
                    ref={inputRef}
                    placeholder="iMessage"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sending || !selectedConversation.lead.phone}
                    rows={1}
                  />
                </div>
                <button
                  className="send-btn"
                  onClick={handleSend}
                  disabled={!newMessage.trim() || sending || !selectedConversation.lead.phone}
                >
                  <Send size={18} />
                </button>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <MessageSquare size={64} />
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the sidebar to view messages</p>
            </div>
          )}
        </div>

        {/* Lead Details Sidebar */}
        {showDetails && selectedConversation && (
          <div className="details-sidebar">
            <div className="details-header">
              <h3>Lead Details</h3>
              <button className="close-details" onClick={() => setShowDetails(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="details-content">
              <div className="details-avatar">
                {getInitials(selectedConversation.lead)}
              </div>
              <h2 className="details-name">
                {selectedConversation.lead.firstName} {selectedConversation.lead.lastName}
              </h2>

              {/* Status badge dropdown */}
              <div className="details-status-row">
                <select
                  className="details-status-select"
                  value={selectedConversation.lead.status}
                  onChange={(e) => {
                    if (selectedConversation.lead.id) {
                      updateLead(selectedConversation.lead.id, { status: e.target.value as Lead['status'] });
                    }
                  }}
                  style={{
                    background: STATUS_OPTIONS.find(s => s.value === selectedConversation.lead.status)?.bg || '#f1f5f9',
                    color: STATUS_OPTIONS.find(s => s.value === selectedConversation.lead.status)?.color || '#334155',
                  }}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Open Portal button */}
              {selectedConversation.lead.id && (
                <button
                  className="details-portal-btn"
                  onClick={() => navigate(`/client/${selectedConversation.lead.id}`)}
                >
                  <ExternalLink size={14} />
                  Open Portal
                </button>
              )}

              <div className="details-section">
                <h4>Contact</h4>
                {selectedConversation.lead.phone && (
                  <div className="details-row">
                    <Phone size={14} />
                    <span>{selectedConversation.lead.phone}</span>
                  </div>
                )}
                {selectedConversation.lead.email && (
                  <div className="details-row">
                    <Mail size={14} />
                    <span>{selectedConversation.lead.email}</span>
                  </div>
                )}
              </div>

              {selectedConversation.lead.location && (
                <div className="details-section">
                  <h4>Location</h4>
                  <div className="details-row">
                    <MapPin size={14} />
                    <span>{selectedConversation.lead.location}</span>
                  </div>
                </div>
              )}

              {selectedConversation.lead.company && (
                <div className="details-section">
                  <h4>Company</h4>
                  <div className="details-row">
                    <Building size={14} />
                    <span>{selectedConversation.lead.company}</span>
                  </div>
                </div>
              )}

              <div className="details-section">
                <h4>Lead Info</h4>
                {selectedConversation.lead.source && (
                  <div className="details-row">
                    <User size={14} />
                    <span>Source: {formatStatus(selectedConversation.lead.source)}</span>
                  </div>
                )}
                {selectedConversation.lead.budget > 0 && (
                  <div className="details-row">
                    <DollarSign size={14} />
                    <span>Budget: {formatBudget(selectedConversation.lead.budget)}</span>
                  </div>
                )}
                {selectedConversation.lead.timeline && (
                  <div className="details-row">
                    <Calendar size={14} />
                    <span>Timeline: {selectedConversation.lead.timeline}</span>
                  </div>
                )}
                {selectedConversation.lead.projectType && (
                  <div className="details-row">
                    <Clock size={14} />
                    <span>Project: {selectedConversation.lead.projectType}</span>
                  </div>
                )}
              </div>

              <InquireProjectInfo lead={selectedConversation.lead} />

              {selectedConversation.lead.notes && (
                <div className="details-section">
                  <h4>Notes</h4>
                  <p className="details-notes">{selectedConversation.lead.notes}</p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="details-section">
                <h4>Quick Actions</h4>

                {/* Engagement Letter - dropdown to select type */}
                <div className="payment-links-selector" style={{ position: 'relative' }}>
                  <button
                    className="payment-link-btn"
                    onClick={() => setShowEngagementSelector(!showEngagementSelector)}
                    disabled={creatingEngagement}
                    title="Create and send engagement letter"
                  >
                    <Link2 size={16} />
                    <span>{creatingEngagement ? 'Creating...' : 'Send Engagement Letter ▾'}</span>
                  </button>
                  {showEngagementSelector && (
                    <div className="payment-links-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      marginTop: '4px',
                      overflow: 'hidden'
                    }}>
                      {ENGAGEMENT_LETTER_TYPES.map(engType => (
                        <button
                          key={engType.id}
                          onClick={() => handleCreateEngagement(engType)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '10px 12px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#334155'
                          }}
                        >
                          <strong>{engType.name}</strong>
                          <span style={{ marginLeft: '8px', color: '#059669', fontWeight: 600 }}>
                            ${engType.fee.toLocaleString()}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Booking Link */}
                {bookingLink ? (
                  <button
                    className="booking-link-btn"
                    onClick={handleLoadBookingLink}
                    title="Send booking link"
                  >
                    <Calendar size={16} />
                    <span>Send Booking Link</span>
                  </button>
                ) : (
                  <button
                    className="booking-link-btn"
                    disabled
                    title="No booking link configured"
                  >
                    <Calendar size={16} />
                    <span>Send Booking Link</span>
                  </button>
                )}
              </div>

              {/* Payments - from QuickBooks */}
              {(selectedConversation.lead as Lead & { lastPaymentAmount?: number; lastPaymentDate?: string }).lastPaymentAmount && (
                <div className="details-section">
                  <h4>
                    <CreditCard size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Payments
                  </h4>
                  <div className="payment-summary">
                    <div className="payment-item">
                      <div className="payment-item-icon">
                        <CheckCircle size={12} />
                      </div>
                      <div className="payment-item-details">
                        <span className="payment-item-amount">
                          {formatBudget((selectedConversation.lead as Lead & { lastPaymentAmount?: number }).lastPaymentAmount || 0)}
                        </span>
                        <span className="payment-item-date">
                          {(selectedConversation.lead as Lead & { lastPaymentDate?: string }).lastPaymentDate || 'Recent'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sendblue Tags */}
              {selectedConversation.lead.phone && (
                <div className="details-section">
                  <h4>SMS Tags</h4>
                  <SendblueTags
                    phoneNumber={selectedConversation.lead.phone}
                    leadId={selectedConversation.lead.id}
                    compact
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaSelect}
        allowedTypes={['image', 'video']}
        title="Select Media to Send"
      />

      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        onConversationStarted={(leadId) => {
          // Find and select the conversation for this lead
          const conv = conversations.find(c => c.leadId === leadId);
          if (conv) {
            handleSelectConversation(conv);
          } else {
            // If conversation doesn't exist yet, wait for it to appear
            // The real-time subscription will pick it up
            setShowMobileList(false);
          }
        }}
      />

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getMessagesQuickSearchResults}
        onSelect={handleMessagesQuickSearchSelect}
        placeholder="Search conversations…"
        ariaLabel="Messages quick search"
        renderIcon={() => <MessageSquare size={16} />}
      />
    </div>
  );
};

export default MessagesPage;
